import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listarProdutos, deletarProduto, criarProduto, atualizarProduto, uploadImagem } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const AdminProdutos = () => {
  const { token } = useAuth();
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    preco: '',
    categoria: 'bolos',
    imagem: ''
  });

  const categorias = [
    { id: 'bolos', nome: 'Bolos' },
    { id: 'bolosAniversarioCasamentos', nome: 'Bolos Aniversário & Casamentos' },
    { id: 'doces', nome: 'Doces' },
    { id: 'salgados', nome: 'Salgados' },
    { id: 'comer', nome: 'Para Comer' }
  ];

  useEffect(() => {
    carregarProdutos();
  }, []);

  const carregarProdutos = async () => {
    try {
      const data = await listarProdutos();
      setProdutos(data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const response = await uploadImagem(file, token);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.url}`;
      setFormData({ ...formData, imagem: imageUrl });
      toast.success('Imagem enviada com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editando) {
        await atualizarProduto(editando.id, formData, token);
        toast.success('Produto atualizado!');
      } else {
        await criarProduto(formData, token);
        toast.success('Produto criado!');
      }
      
      setDialogAberto(false);
      resetForm();
      carregarProdutos();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    }
  };

  const handleEditar = (produto) => {
    setEditando(produto);
    setFormData({
      nome: produto.nome,
      descricao: produto.descricao,
      preco: produto.preco,
      categoria: produto.categoria,
      imagem: produto.imagem
    });
    setDialogAberto(true);
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este produto?')) return;

    try {
      await deletarProduto(id, token);
      toast.success('Produto deletado!');
      carregarProdutos();
    } catch (error) {
      toast.error('Erro ao deletar produto');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      preco: '',
      categoria: 'bolos',
      imagem: ''
    });
    setEditando(null);
  };

  const handleNovo = () => {
    resetForm();
    setDialogAberto(true);
  };

  return (
    <div className="admin-produtos">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Gestão de Produtos</h2>
        <Button onClick={handleNovo} className="btn-primary">
          <Plus size={20} /> Novo Produto
        </Button>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div className="admin-produtos-grid">
          {produtos.map((produto) => (
            <Card key={produto.id} className="admin-produto-card">
              <div className="admin-produto-imagem">
                <img src={produto.imagem} alt={produto.nome} />
              </div>
              <CardContent className="admin-produto-content">
                <h3>{produto.nome}</h3>
                <p className="produto-categoria">{categorias.find(c => c.id === produto.categoria)?.nome}</p>
                <p className="produto-preco">{produto.preco}</p>
                <div className="admin-produto-actions">
                  <Button onClick={() => handleEditar(produto)} variant="outline" size="sm">
                    <Edit size={16} /> Editar
                  </Button>
                  <Button onClick={() => handleDeletar(produto.id)} variant="destructive" size="sm">
                    <Trash2 size={16} /> Deletar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog para criar/editar */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label>Nome *</label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Descrição *</label>
              <Textarea
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Preço *</label>
                <Input
                  value={formData.preco}
                  onChange={(e) => setFormData({ ...formData, preco: e.target.value })}
                  placeholder="€10.00"
                  required
                />
              </div>

              <div className="form-group">
                <label>Categoria *</label>
                <Select value={formData.categoria} onValueChange={(value) => setFormData({ ...formData, categoria: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="form-group">
              <label>Imagem</label>
              <div className="image-upload-container">
                {formData.imagem && (
                  <img src={formData.imagem} alt="Preview" className="image-preview" />
                )}
                <label htmlFor="image-upload" className="image-upload-btn">
                  <Upload size={20} />
                  {uploadingImage ? 'Enviando...' : 'Escolher Imagem'}
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogAberto(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editando ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProdutos;
